import type { ResultColumn, ResultEnvelope, Row, Visualization } from "@cd/core";
import { formatRange, formatTimeBucket, formatValue, humanize } from "./format";
import { GRADIENT_SCHEMES, gradientDomain, isGradientScheme } from "./gradient";
import { DIM_OPACITY, isDimmed, type Highlight } from "./highlight";

export interface ChartTheme {
  /** Categorical slots, assigned in fixed order (never cycled past 8). */
  palette: string[];
  /** De-emphasis ink for comparison-period series. */
  comparison: string;
  text: string;
  muted: string;
  grid: string;
  background: string;
  tooltipBg: string;
  tooltipBorder: string;
}

type Option = Record<string, unknown>;

/** How a grouping value is labelled on axes, legends and tiles. */
export const groupLabel = (col: ResultColumn, v: unknown) =>
  col.type === "time" ? formatTimeBucket(v, col.grain) : humanize(v);
const label = groupLabel;

function baseOption(theme: ChartTheme): Option {
  return {
    color: theme.palette,
    backgroundColor: "transparent",
    textStyle: { color: theme.text, fontFamily: "inherit" },
    animationDuration: 300,
    grid: { left: 8, right: 16, top: 36, bottom: 8, containLabel: true },
    legend: { top: 0, left: 0, icon: "roundRect", itemWidth: 10, itemHeight: 10, textStyle: { color: theme.muted } },
    tooltip: {
      trigger: "axis",
      backgroundColor: theme.tooltipBg,
      borderColor: theme.tooltipBorder,
      textStyle: { color: theme.text },
      confine: true,
    },
  };
}

function axisCommon(theme: ChartTheme) {
  return {
    axisLine: { lineStyle: { color: theme.grid } },
    axisTick: { show: false },
    axisLabel: { color: theme.muted, hideOverlap: true },
    splitLine: { lineStyle: { color: theme.grid, width: 1, type: "solid" as const } },
  };
}

const MAX_SERIES = 8;

/** Optional text config: trimmed, or undefined when blank. */
const text = (v: unknown) => (typeof v === "string" && v.trim() ? v.trim() : undefined);

/**
 * Axis title. Left-axis titles sit above the axis as horizontal text (no guessing tick-label
 * widths); on an inverted axis the top is its "start".
 */
function axisTitle(theme: ChartTheme, name: string | undefined, side: "bottom" | "left" | "right", inverse = false): Option {
  if (!name) return {};
  const nameTextStyle = { color: theme.muted, fontSize: 11, fontWeight: 500 };
  if (side === "bottom") return { name, nameLocation: "middle", nameGap: 28, nameTextStyle };
  return side === "left"
    ? { name, nameLocation: inverse ? "start" : "end", nameGap: 12, nameTextStyle: { ...nameTextStyle, align: "left", padding: [0, 0, 0, -4] } }
    : { name, nameLocation: "end", nameGap: 12, nameTextStyle: { ...nameTextStyle, align: "right", padding: [0, -4, 0, 0] } };
}

/**
 * Categorical color never cycles: past 8 categories, fold the tail into
 * "Other" — but only for additive metrics (summing distinct counts or
 * averages would be wrong). Non-additive metrics keep the top 8.
 */
export function foldCategories<T>(
  groups: [string, T[]][],
  total: (rows: T[]) => number,
  additive: boolean,
  merge: (rows: T[]) => T[],
): { groups: [string, T[]][]; folded: number } {
  if (groups.length <= MAX_SERIES) return { groups, folded: 0 };
  const ranked = [...groups].sort((a, b) => total(b[1]) - total(a[1]));
  if (!additive) return { groups: ranked.slice(0, MAX_SERIES), folded: ranked.length - MAX_SERIES };
  const head = ranked.slice(0, MAX_SERIES - 1);
  const tail = ranked.slice(MAX_SERIES - 1);
  return { groups: [...head, ["Other", merge(tail.flatMap(([, r]) => r))]], folded: tail.length };
}

/**
 * Build an ECharts option for line/area/bar/combo/pie/heatmap/scatter/treemap/funnel. Pure — no DOM, easy to test.
 * `highlight` dims every grouping value it doesn't list.
 */
export function buildChartOption(viz: Visualization, res: ResultEnvelope, theme: ChartTheme, highlight: readonly Highlight[] = []): Option {
  const dims = res.columns.filter((c) => c.kind === "dimension");
  const metrics = res.columns.filter((c) => c.kind === "metric");
  const opt = baseOption(theme);
  const showLegend = viz.config.legend !== false;
  const dataLabels = viz.config.dataLabels === true;
  const fmt = (m: ResultColumn) => (v: number) => formatValue(v, m.format, { axis: true });

  if (viz.type === "heatmap") return heatmapOption(viz, res, theme, opt, highlight);
  if (viz.type === "scatter") return scatterOption(viz, res, theme, opt, highlight);
  if (viz.type === "treemap") return treemapOption(viz, res, theme, opt, highlight);
  if (viz.type === "funnel") return funnelOption(res, theme, opt);

  if (viz.type === "pie") {
    const d = dims[0]!;
    const m = metrics[0]!;
    const { groups } = foldCategories(
      res.rows.map((r): [string, Row[]] => [label(d, r[d.key]), [r]]),
      (rows) => rows.reduce((a, r) => a + ((r[m.key] as number) ?? 0), 0),
      m.additive === true,
      (rows) => [{ [m.key]: rows.reduce((a, r) => a + ((r[m.key] as number) ?? 0), 0) }],
    );
    return {
      ...opt,
      tooltip: { ...(opt.tooltip as Option), trigger: "item", valueFormatter: (v: number) => formatValue(v, m.format) },
      legend: { ...(opt.legend as Option), show: showLegend, type: "scroll" },
      series: [
        {
          type: "pie", radius: dataLabels ? ["45%", "68%"] : ["55%", "80%"], center: ["50%", showLegend ? "56%" : "50%"], avoidLabelOverlap: true,
          // 2px surface gap between segments; no stroke borders.
          itemStyle: { borderColor: theme.background, borderWidth: 2, borderRadius: 4 },
          label: dataLabels ? { show: true, color: theme.muted, formatter: "{b}: {d}%" } : { show: false },
          labelLine: { lineStyle: { color: theme.grid } },
          data: groups.map(([name, rows]) => ({
            name, value: rows[0]![m.key],
            ...(isDimmed(highlight, d.key, rows[0]![d.key]) ? { itemStyle: { opacity: DIM_OPACITY } } : {}),
          })),
        },
      ],
    };
  }

  const [xDim, seriesDim] = dims;
  if (!xDim) return opt;
  const xs: string[] = [];
  const xIndex = new Map<string, number>();
  const xDimmed: boolean[] = [];
  for (const r of res.rows) {
    const k = String(r[xDim.key]);
    if (!xIndex.has(k)) { xIndex.set(k, xs.length); xs.push(k); xDimmed.push(isDimmed(highlight, xDim.key, r[xDim.key] ?? null)); }
  }
  const xLabels = xs.map((x) => label(xDim, x === "null" ? null : x));
  const isBar = viz.type === "bar";
  const isCombo = viz.type === "combo";
  const horizontal = isBar && viz.config.horizontal === true;
  const stacked = (viz.config.stacked === true && !isCombo) || viz.type === "area";
  const m0 = metrics[0]!;
  // Combo: the first metric as bars, the rest as lines. Lines move to a right-hand axis only when
  // their unit differs (sales vs orders); same-unit metrics share one scale.
  const barFor = (mi: number) => isBar || (isCombo && mi === 0);
  const dualAxis = isCombo && metrics.slice(1).some((m) => m.format !== m0.format);
  const axisOf = (m: ResultColumn) => (dualAxis && m.format !== m0.format ? 1 : 0);
  const series: Option[] = [];

  const valueLabel = (m: ResultColumn, bar: boolean): Option | undefined =>
    dataLabels
      ? {
          show: true, color: theme.muted, fontSize: 11,
          position: bar ? (stacked ? "inside" : horizontal ? "right" : "top") : "top",
          ...(bar && stacked ? { color: "#fff" } : {}),
          formatter: (p: { value: unknown }) => (p.value === null || p.value === undefined ? "" : formatValue(p.value, m.format, { compact: true })),
        }
      : undefined;

  /** `dimmed`: the whole series is a value the highlight leaves out. */
  const mkSeries = (name: string, rows: Row[], key: string, m: ResultColumn, bar = isBar, dimmed = false) => {
    const data: (number | null | Option)[] = xs.map(() => null);
    rows.forEach((r) => {
      const i = xIndex.get(String(r[xDim.key]));
      if (i !== undefined) data[i] = r[key] as number | null;
    });
    // Bars dim one by one; a line can't, so its highlighted x values get a band instead (below).
    if (bar) xDimmed.forEach((d, i) => { if (d) data[i] = { value: data[i], itemStyle: { opacity: DIM_OPACITY } }; });
    const opacity = dimmed ? DIM_OPACITY : undefined;
    return {
      name, type: bar ? "bar" : "line", data, showSymbol: !bar && hasLonePoint(data), symbolSize: 8, smooth: 0.2,
      stack: stacked ? "total" : undefined,
      ...(isCombo ? { yAxisIndex: axisOf(m), tooltip: { valueFormatter: (v: number) => formatValue(v, m.format) } } : {}),
      lineStyle: bar ? undefined : { width: 2, cap: "round", join: "round", opacity },
      areaStyle: viz.type === "area" ? { opacity: dimmed ? 0.03 : 0.1 } : undefined,
      // Bars: <=24px, 4px rounded data-end, square at the baseline, 2px surface gap in stacks.
      barMaxWidth: 24,
      itemStyle: bar
        ? {
            borderRadius: horizontal ? [0, 4, 4, 0] : [4, 4, 0, 0],
            ...(stacked ? { borderColor: theme.background, borderWidth: 1 } : {}),
            opacity,
          }
        : { borderColor: theme.background, borderWidth: 2, opacity },
      label: valueLabel(m, bar),
      emphasis: { focus: "series" },
    };
  };

  if (seriesDim) {
    const m = metrics[0]!;
    const groups = new Map<string, Row[]>();
    for (const r of res.rows) {
      const k = label(seriesDim, r[seriesDim.key]);
      if (!groups.has(k)) groups.set(k, []);
      groups.get(k)!.push(r);
    }
    const folded = foldCategories(
      [...groups],
      (rows) => rows.reduce((a, r) => a + ((r[m.key] as number) ?? 0), 0),
      m.additive === true,
      (rows) => {
        const byX = new Map<string, Row>();
        for (const r of rows) {
          const k = String(r[xDim.key]);
          const acc: Row = byX.get(k) ?? { [xDim.key]: r[xDim.key] ?? null, [m.key]: 0 };
          acc[m.key] = ((acc[m.key] as number) ?? 0) + ((r[m.key] as number) ?? 0);
          byX.set(k, acc);
        }
        return [...byX.values()];
      },
    );
    for (const [name, rows] of folded.groups) series.push(mkSeries(name, rows, m.key, m, isBar, isDimmed(highlight, seriesDim.key, rows[0]![seriesDim.key])));
  } else {
    metrics.forEach((m, mi) => series.push(mkSeries(m.label, res.rows, m.key, m, barFor(mi))));
    // Comparison period, in de-emphasis ink. Time buckets align by position (day N vs day N of
    // the prior range); categories and weekdays align by value (Monday vs Monday).
    // Older periods fade further.
    const comparisons = res.comparisons ?? (res.compareRows ? [{ timeRange: res.compareTimeRange, rows: res.compareRows }] : []);
    comparisons.forEach((cmp, ci) => {
      const prevByKey = new Map(cmp.rows.map((r) => [String(r[xDim.key]), r]));
      const prevLabel = cmp.timeRange ? formatRange(cmp.timeRange) : "previous";
      const opacity = 1 - (0.6 * ci) / Math.max(comparisons.length - 1, 1);
      metrics.forEach((m, mi) => {
        const data = xs.map((x, i) =>
          ((xDim.type === "time" && xDim.grain !== "day_of_week" ? cmp.rows[i] : prevByKey.get(x))?.[m.key] as number | null | undefined) ?? null,
        );
        const combo = isCombo ? { yAxisIndex: axisOf(m), tooltip: { valueFormatter: (v: number) => formatValue(v, m.format) } } : {};
        series.push(
          barFor(mi)
            ? {
                ...combo,
                name: `${m.label} (${prevLabel})`, type: "bar", data, barMaxWidth: 24, color: theme.comparison,
                itemStyle: { color: theme.comparison, opacity, borderRadius: horizontal ? [0, 4, 4, 0] : [4, 4, 0, 0] },
                stack: stacked ? `previous-${ci}` : undefined,
              }
            : {
                ...combo,
                name: `${m.label} (${prevLabel})`, type: "line", data, showSymbol: hasLonePoint(data), smooth: 0.2,
                color: theme.comparison, lineStyle: { type: "dashed", width: 2, color: theme.comparison, opacity },
              },
        );
      });
    });
  }

  // Lines only: a band behind each highlighted x value, since a line can't dim part of itself.
  const band = xLabels.filter((_, i) => !xDimmed[i]);
  if (series[0] && !series.some((s) => s.type === "bar") && band.length < xLabels.length) {
    series[0] = {
      ...series[0],
      markArea: { silent: true, itemStyle: { color: theme.grid, opacity: 0.6 }, data: band.map((x) => [{ xAxis: x }, { xAxis: x }]) },
    };
  }

  // A single x value centers rather than sitting on the axis edge.
  const categoryAxis = { type: "category", data: xLabels, boundaryGap: isBar || isCombo || xLabels.length === 1, ...axisCommon(theme), splitLine: { show: false } };
  const valueAxis = { type: "value", ...axisCommon(theme), axisLabel: { color: theme.muted, formatter: fmt(m0) } };
  // Titles name the axis on screen: "X" is always the bottom axis, "Y" the left, in either orientation.
  // With two scales, each axis is titled by default so neither reads as the other's.
  const right = dualAxis ? metrics.find((m) => axisOf(m) === 1)! : undefined;
  const xTitle = text(viz.config.xAxisLabel);
  const yTitle = text(viz.config.yAxisLabel) ?? (right ? m0.label : undefined);
  const y2Title = right ? (text(viz.config.y2AxisLabel) ?? right.label) : undefined;
  const legendShown = showLegend && series.length > 1;
  const grid = opt.grid as Option;
  return {
    ...opt,
    // containLabel reserves tick labels, not axis names, so make room for titles explicitly.
    grid: {
      ...grid,
      top: (legendShown ? 36 : 16) + (yTitle || y2Title ? 18 : 0) + (dataLabels && !horizontal ? 8 : 0),
      bottom: 8 + (xTitle ? 22 : 0),
      right: dataLabels && horizontal ? 40 : grid.right,
    },
    tooltip: { ...(opt.tooltip as Option), valueFormatter: (v: number) => formatValue(v, m0.format) },
    legend: { ...(opt.legend as Option), show: legendShown, type: "scroll" },
    xAxis: { ...(horizontal ? valueAxis : categoryAxis), ...axisTitle(theme, xTitle, "bottom") },
    yAxis: horizontal
      ? { ...categoryAxis, inverse: true, ...axisTitle(theme, yTitle, "left", true) }
      : right
        ? [
            { ...valueAxis, alignTicks: true, ...axisTitle(theme, yTitle, "left") },
            {
              ...valueAxis, alignTicks: true, splitLine: { show: false },
              axisLabel: { color: theme.muted, formatter: fmt(right) }, ...axisTitle(theme, y2Title, "right"),
            },
          ]
        : { ...valueAxis, ...axisTitle(theme, yTitle, "left") },
    series,
  };
}

/** Tooltip formatters return HTML, so category values are escaped. */
const esc = (s: string) => s.replace(/[&<>"']/g, (c) => `&#${c.charCodeAt(0)};`);

const num = (v: unknown) => (typeof v === "number" && Number.isFinite(v) ? v : null);

/** A value with no neighbor to draw a segment to: a line hides its dots, so this one would be invisible. */
const hasLonePoint = (data: unknown[]) =>
  data.some((v, i) => v !== null && v !== undefined && (data[i - 1] ?? null) === null && (data[i + 1] ?? null) === null);

/**
 * Distinct values of a column in display order: dates and numbers ascending. Categories keep query
 * order unless `sortAll` — a heatmap is read by position, so "1 Mon … 7 Sun" must stay in sequence.
 */
export function distinctValues(rows: Row[], col: ResultColumn, sortAll = false): unknown[] {
  const seen = new Map<string, unknown>();
  for (const r of rows) {
    const v = r[col.key] ?? null;
    if (!seen.has(String(v))) seen.set(String(v), v);
  }
  const vals = [...seen.values()];
  if (col.type === "time" || col.type === "number")
    vals.sort((a, b) => (a === null ? 1 : b === null ? -1 : (a as number | string) < (b as number | string) ? -1 : a === b ? 0 : 1));
  else if (sortAll)
    vals.sort((a, b) => (a === null ? 1 : b === null ? -1 : String(a).localeCompare(String(b), "en", { numeric: true })));
  return vals;
}

/** `#rrggbb` blend for the canvas, which can't paint `color-mix()`. */
function blend(from: string, to: string, t: number): string {
  const rgb = (h: string) => [1, 3, 5].map((i) => parseInt(h.slice(i, i + 2), 16));
  const [a, b] = [rgb(from), rgb(to)];
  return `#${a.map((x, i) => Math.round(x + (b[i]! - x) * t).toString(16).padStart(2, "0")).join("")}`;
}

/**
 * Heatmap: first grouping across, second down, one metric as cell color. Uses the same schemes as
 * table/pivot color scales — sequential is one hue from near-surface (the lowest cell) to full
 * (the highest); diverging is two hues around a neutral midpoint, centered on 0 when the data spans it.
 */
function heatmapOption(viz: Visualization, res: ResultEnvelope, theme: ChartTheme, opt: Option, highlight: readonly Highlight[]): Option {
  const [xDim, yDim] = res.columns.filter((c) => c.kind === "dimension");
  const m = res.columns.find((c) => c.kind === "metric")!;
  if (!xDim || !yDim) return opt;
  const xs = distinctValues(res.rows, xDim, true);
  const ys = distinctValues(res.rows, yDim, true);
  const xi = new Map(xs.map((v, i) => [String(v), i]));
  const yi = new Map(ys.map((v, i) => [String(v), i]));
  const xLabels = xs.map((v) => label(xDim, v));
  const yLabels = ys.map((v) => label(yDim, v));
  const data = res.rows.flatMap((r) => {
    const v = num(r[m.key]);
    return v === null ? [] : [[xi.get(String(r[xDim.key] ?? null))!, yi.get(String(r[yDim.key] ?? null))!, v]];
  });
  const dimmed = res.rows.filter((r) => num(r[m.key]) !== null).map((r) => isDimmed(highlight, xDim.key, r[xDim.key] ?? null) || isDimmed(highlight, yDim.key, r[yDim.key] ?? null));

  const schemeId = isGradientScheme(viz.config.colorScale) && viz.config.colorScale !== "none" ? viz.config.colorScale : "blue";
  const scheme = GRADIENT_SCHEMES.find((g) => g.id === schemeId)!;
  const domain = gradientDomain(data.map((d) => d[2]!)) ?? { min: 0, max: 0, mid: 0 };
  let min = domain.min, max = domain.max, colors: string[];
  if ("low" in scheme) {
    const span = Math.max(domain.max - domain.mid, domain.mid - domain.min);
    [min, max] = [domain.mid - span, domain.mid + span];
    colors = [theme.palette[scheme.low]!, theme.grid, theme.palette[scheme.high]!];
  } else {
    // The lightest step is the lowest cell, not 0: a heatmap is read for contrast between cells.
    // All-equal data would collapse the scale, so it falls back to starting at 0.
    if (min === max) min = Math.min(0, min - 1);
    const hue = theme.palette["slot" in scheme ? scheme.slot : 0]!;
    colors = [blend(theme.background, hue, 0.08), hue];
  }
  if (viz.config.colorReverse === true) colors.reverse();

  const showLegend = viz.config.legend !== false;
  const xTitle = text(viz.config.xAxisLabel);
  const yTitle = text(viz.config.yAxisLabel);
  const axis = { ...axisCommon(theme), splitLine: { show: false }, axisLine: { show: false } };
  return {
    ...opt,
    grid: { ...(opt.grid as Option), top: 8 + (yTitle ? 18 : 0), bottom: (showLegend ? 40 : 8) + (xTitle ? 22 : 0) },
    legend: { show: false },
    tooltip: {
      ...(opt.tooltip as Option), trigger: "item",
      formatter: (p: { value: number[] }) =>
        `${esc(xLabels[p.value[0]!]!)} · ${esc(yLabels[p.value[1]!]!)}<br/>${esc(m.label)}: <b>${esc(formatValue(p.value[2], m.format))}</b>`,
    },
    visualMap: {
      type: "continuous", show: showLegend, min, max, calculable: false, orient: "horizontal", left: "center", bottom: 0,
      itemWidth: 10, itemHeight: 140, inRange: { color: colors }, textStyle: { color: theme.muted, fontSize: 11 },
      text: [formatValue(max, m.format, { axis: true }), formatValue(min, m.format, { axis: true })], textGap: 8,
    },
    xAxis: { type: "category", data: xLabels, ...axis, ...axisTitle(theme, xTitle, "bottom") },
    yAxis: { type: "category", data: yLabels, inverse: true, ...axis, ...axisTitle(theme, yTitle, "left", true) },
    series: [{
      type: "heatmap", data: data.map((value, i) => (dimmed[i] ? { value, itemStyle: { opacity: DIM_OPACITY } } : value)),
      // 2px surface gap between cells.
      itemStyle: { borderColor: theme.background, borderWidth: 2, borderRadius: 3 },
      label: viz.config.dataLabels === true
        ? { show: true, color: theme.text, fontSize: 11, formatter: (p: { value: number[] }) => formatValue(p.value[2], m.format, { compact: true }) }
        : { show: false },
      emphasis: { itemStyle: { borderColor: theme.text, borderWidth: 1 } },
    }],
  };
}

/**
 * Scatter: one point per value of the first grouping, metric 1 across and metric 2 up. An optional
 * second grouping colors the points (folded past 8); an optional third metric sizes them by area.
 */
function scatterOption(viz: Visualization, res: ResultEnvelope, theme: ChartTheme, opt: Option, highlight: readonly Highlight[]): Option {
  const [pointDim, groupDim] = res.columns.filter((c) => c.kind === "dimension");
  const [mx, my, ms] = res.columns.filter((c) => c.kind === "metric");
  if (!pointDim || !mx || !my) return opt;
  type Point = { name: string; value: (number | null)[]; itemStyle?: Option };
  const points = res.rows.flatMap((r): [string, Point][] => {
    const [x, y] = [num(r[mx.key]), num(r[my.key])];
    if (x === null || y === null) return [];
    const dim = isDimmed(highlight, pointDim.key, r[pointDim.key] ?? null) ? { itemStyle: { opacity: DIM_OPACITY } } : {};
    return [[groupDim ? label(groupDim, r[groupDim.key]) : mx.label, { name: label(pointDim, r[pointDim.key]), value: [x, y, ms ? num(r[ms.key]) : null], ...dim }]];
  });
  const byGroup = new Map<string, Point[]>();
  for (const [g, p] of points) byGroup.set(g, [...(byGroup.get(g) ?? []), p]);
  // A group's raw value, for the highlight: groups are keyed by label.
  const groupValue = new Map(groupDim ? res.rows.map((r) => [label(groupDim, r[groupDim.key]), r[groupDim.key] ?? null] as const) : []);
  // "Other" collects the points of the smallest groups; nothing is summed.
  const { groups } = foldCategories([...byGroup], (pts) => pts.length, true, (pts) => pts);

  const maxSize = Math.max(0, ...points.map(([, p]) => Math.abs(p.value[2] ?? 0)));
  const size = (v: (number | null)[]) => (ms && maxSize > 0 ? 8 + 32 * Math.sqrt(Math.abs(v[2] ?? 0) / maxSize) : 10);
  const showLegend = viz.config.legend !== false && groups.length > 1;
  const xTitle = text(viz.config.xAxisLabel) ?? mx.label;
  const yTitle = text(viz.config.yAxisLabel) ?? my.label;
  const valueAxis = (m: ResultColumn) => ({ type: "value", scale: true, ...axisCommon(theme), axisLabel: { color: theme.muted, formatter: (v: number) => formatValue(v, m.format, { axis: true }) } });
  const line = (m: ResultColumn, v: number | null | undefined) => `${esc(m.label)}: <b>${esc(formatValue(v, m.format))}</b>`;
  return {
    ...opt,
    grid: { ...(opt.grid as Option), top: (showLegend ? 36 : 16) + 18, bottom: 30 },
    legend: { ...(opt.legend as Option), show: showLegend, type: "scroll" },
    tooltip: {
      ...(opt.tooltip as Option), trigger: "item",
      formatter: (p: { name: string; seriesName: string; value: (number | null)[] }) =>
        [`${esc(p.name)}${groupDim ? ` · ${esc(p.seriesName)}` : ""}`, line(mx, p.value[0]), line(my, p.value[1]), ...(ms ? [line(ms, p.value[2])] : [])].join("<br/>"),
    },
    xAxis: { ...valueAxis(mx), ...axisTitle(theme, xTitle, "bottom") },
    yAxis: { ...valueAxis(my), ...axisTitle(theme, yTitle, "left") },
    series: groups.map(([name, data]) => ({
      name, type: "scatter", data, symbolSize: size,
      // 2px surface ring so overlapping points stay separable.
      itemStyle: { borderColor: theme.background, borderWidth: 2, opacity: groupDim && isDimmed(highlight, groupDim.key, groupValue.get(name)) ? DIM_OPACITY : 0.9 },
      label: viz.config.dataLabels === true ? { show: true, formatter: "{b}", position: "right", color: theme.muted, fontSize: 11 } : { show: false },
      labelLayout: { hideOverlap: true },
      emphasis: { focus: "series" },
    })),
  };
}

/**
 * Treemap: tile area is share of the total, so the metric must be additive (the registry checks).
 * A second grouping nests inside the first. Past 8 top-level tiles the smallest fold into "Other";
 * zero and negative values can't have an area and are left out.
 */
function treemapOption(viz: Visualization, res: ResultEnvelope, theme: ChartTheme, opt: Option, highlight: readonly Highlight[]): Option {
  const [outer, inner] = res.columns.filter((c) => c.kind === "dimension");
  const m = res.columns.find((c) => c.kind === "metric")!;
  if (!outer) return opt;
  const val = (r: Row) => Math.max(num(r[m.key]) ?? 0, 0);
  const sum = (rows: Row[]) => rows.reduce((a, r) => a + val(r), 0);
  const byInner = (rows: Row[]) => {
    const acc = new Map<string, number>();
    for (const r of rows) {
      const k = label(inner!, r[inner!.key]);
      acc.set(k, (acc.get(k) ?? 0) + val(r));
    }
    return [...acc].filter(([, v]) => v > 0);
  };
  // Tiles are keyed by label; these map back to raw values for the highlight. Tiles are colored by
  // level, so a dimmed one is blended toward the background rather than made transparent.
  const rawOf = (col: ResultColumn) => new Map(res.rows.map((r) => [label(col, r[col.key]), r[col.key] ?? null] as const));
  const outerRaw = rawOf(outer);
  const innerRaw = inner ? rawOf(inner) : new Map<string, Row[string]>();
  const fade = (color: string) => blend(theme.background, color, DIM_OPACITY);
  const byOuter = new Map<string, Row[]>();
  for (const r of res.rows) {
    const k = label(outer, r[outer.key]);
    byOuter.set(k, [...(byOuter.get(k) ?? []), r]);
  }
  const { groups, folded } = foldCategories([...byOuter], sum, true, (rows) =>
    inner ? byInner(rows).map(([k, v]) => ({ [inner.key]: k, [m.key]: v })) : [{ [m.key]: sum(rows) }],
  );
  const total = groups.reduce((a, [, rows]) => a + sum(rows), 0);
  const data = groups
    .map(([name, rows], i) => {
      const base = folded && i === groups.length - 1 ? theme.comparison : theme.palette[i]!;
      const color = isDimmed(highlight, outer.key, outerRaw.get(name)) ? fade(base) : base;
      return inner
        ? {
            name, itemStyle: { color },
            children: byInner(rows).map(([n, value]) => ({
              name: n, value, ...(isDimmed(highlight, inner.key, innerRaw.get(n)) ? { itemStyle: { color: fade(color) } } : {}),
            })),
          }
        : { name, value: sum(rows), itemStyle: { color } };
    })
    .filter((n) => ("children" in n ? n.children!.length : n.value > 0));

  const showValues = viz.config.dataLabels === true;
  const tileLabel = {
    show: true, color: "#fff", fontSize: 11, overflow: "truncate",
    formatter: (p: { name: string; value: number }) => (showValues ? `${p.name}\n${formatValue(p.value, m.format, { compact: true })}` : p.name),
  };
  const tile = { borderColor: theme.background, gapWidth: 2, borderRadius: 4 };
  return {
    ...opt,
    legend: { show: false },
    tooltip: {
      ...(opt.tooltip as Option), trigger: "item",
      formatter: (p: { value: number; treePathInfo: { name: string }[] }) => {
        const path = p.treePathInfo.slice(1).map((n) => esc(n.name)).join(" › ");
        const share = total > 0 ? ` (${formatValue((p.value / total) * 100, "percent")})` : "";
        return `${path}<br/>${esc(m.label)}: <b>${esc(formatValue(p.value, m.format))}</b>${share}`;
      },
    },
    series: [{
      type: "treemap", name: m.label, data, top: 0, left: 0, right: 0, bottom: 0,
      roam: false, nodeClick: false, breadcrumb: { show: false }, label: tileLabel,
      levels: inner
        ? [
            { itemStyle: { borderColor: theme.background, borderWidth: 0, gapWidth: 2 } },
            { itemStyle: { ...tile, borderWidth: 2 }, upperLabel: { show: true, height: 20, color: theme.text, fontSize: 11, fontWeight: 600 } },
            { colorAlpha: [0.6, 1], itemStyle: { ...tile, gapWidth: 1, borderRadius: 2 } },
          ]
        : [{ itemStyle: { borderColor: theme.background, borderWidth: 0, gapWidth: 2 } }, { itemStyle: tile }],
    }],
  };
}

/**
 * Funnel: one horizontal bar per metric, top to bottom in query order, on a shared linear axis. Late
 * stages are often tiny next to the first (sign-ups → redemptions), so a tapered funnel shape would
 * hide them; every bar is labeled with its value and its conversion from the first and previous stage.
 */
function funnelOption(res: ResultEnvelope, theme: ChartTheme, opt: Option): Option {
  const metrics = res.columns.filter((c) => c.kind === "metric");
  const row = res.rows[0] ?? {};
  const values = metrics.map((m) => num(row[m.key]));
  const first = values[0];
  const pct = (v: number | null, of: number | null | undefined) =>
    v !== null && of ? formatValue((v / of) * 100, "percent") : null;
  const notes = values.map((v, i) =>
    i === 0 ? "" : [pct(v, first) && `${pct(v, first)} of ${metrics[0]!.label.toLowerCase()}`, i > 1 && pct(v, values[i - 1]) && `${pct(v, values[i - 1])} of previous`]
      .filter(Boolean).join(" · "),
  );
  return {
    ...opt,
    legend: { show: false },
    grid: { left: 8, right: 16, top: 8, bottom: 8, containLabel: true },
    tooltip: {
      ...(opt.tooltip as Option), trigger: "item",
      formatter: (p: { dataIndex: number }) => {
        const m = metrics[p.dataIndex]!;
        const note = notes[p.dataIndex];
        return `${esc(m.label)}: <b>${esc(formatValue(values[p.dataIndex], m.format))}</b>${note ? `<br/>${esc(note)}` : ""}`;
      },
    },
    // Room past the longest bar for its label.
    xAxis: { type: "value", show: false, max: (v: { max: number }) => v.max * 1.7 },
    yAxis: {
      type: "category", inverse: true, data: metrics.map((m) => m.label),
      ...axisCommon(theme), axisLine: { show: false }, axisLabel: { color: theme.text, fontWeight: 500 },
    },
    series: [{
      type: "bar", barCategoryGap: "28%",
      data: values.map((v) => ({ value: v, itemStyle: { color: theme.palette[0], borderRadius: 4 } })),
      label: {
        show: true, position: "right", distance: 8,
        formatter: (p: { dataIndex: number }) => {
          const v = formatValue(values[p.dataIndex], metrics[p.dataIndex]!.format);
          return notes[p.dataIndex] ? `{v|${v}}\n{n|${notes[p.dataIndex]}}` : `{v|${v}}`;
        },
        rich: { v: { fontWeight: 600, color: theme.text, fontSize: 12 }, n: { color: theme.muted, fontSize: 11, padding: [2, 0, 0, 0] } },
      },
    }],
  };
}
